/* eslint-disable @typescript-eslint/no-explicit-any */
// After a successful login, the API access token (short-lived) and refresh token (long-lived) are returned.
// After the token expires, you'll need to request a new token using the refresh token.
// Then, you need to use the freshly retrieved token for the next API requests until that token will expire as well.
// http://blog.logicwind.com/content/images/2020/05/WPQA0.png
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios, { AxiosResponse } from 'axios';

import { Loader } from '../components/Loader/Loader';
import { ACCESS_TOKEN_LS_KEY, REFRESH_TOKEN_LS_KEY, TOKEN_EXPIRED_RESPONSE_MESSAGE } from '../constants';

// TODO: Should be generic
export interface User {
  firstName: string;
  lastName: string;
  email?: string;
  created?: string;
  login?: string;
  paymentStatus?: 'FREE' | 'PAID' | 'NOT_PAID';
  phone?: number | null;
  stripeCustomerId?: number | null;
  analyticsId?: string;
  onboarded?: boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthContextProps {
  user: User | null;
  loginUser: ({ login, password }: { login: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserToken: () => Promise<string | undefined>;
  getUserProfile: VoidFunction;
}
export const AuthContext = createContext({} as AuthContextProps);

function setAuthTokens(tokens: AuthTokens) {
  localStorage.setItem(ACCESS_TOKEN_LS_KEY, tokens?.accessToken);
  localStorage.setItem(REFRESH_TOKEN_LS_KEY, tokens?.refreshToken);
}

function hasAuthTokensStored() {
  return !!(localStorage.getItem(ACCESS_TOKEN_LS_KEY) && localStorage.getItem(REFRESH_TOKEN_LS_KEY));
}

interface AuthProviderProps {
  onRefreshUserToken: ({ refreshToken }: { refreshToken: string }) => Promise<AxiosResponse<AuthTokens, any>>;
  onLogin: ({ login, password }: { login: string; password: string }) => Promise<AxiosResponse<AuthTokens, any>>;
  onGetUser: () => Promise<AxiosResponse<User, any>>;
  children: ReactNode;
}

export const AuthProvider = ({ children, onRefreshUserToken, onGetUser, onLogin }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(hasAuthTokensStored());

  const clearAuthData = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_LS_KEY);
    localStorage.removeItem(REFRESH_TOKEN_LS_KEY);
    setUser(null);
  }, []);

  // eslint-disable-next-line consistent-return
  const refreshUserToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_LS_KEY);
      if (refreshToken) {
        setLoading(true);
        const { data } = await onRefreshUserToken({
          refreshToken,
        });
        setAuthTokens(data);
        return data.accessToken;
      }
    } catch (e) {
      clearAuthData();
    } finally {
      setLoading(false);
    }
  }, [clearAuthData, onRefreshUserToken]);

  const logout = useCallback(async () => {
    clearAuthData();
  }, [clearAuthData]);

  const getUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await onGetUser();
      setUser(response.data);
    } catch (e) {
      // This is the single request for which we need to handle token expiration manually (it fires before axios interceptors are set)
      if (axios.isAxiosError(e) && e?.response?.data?.message === TOKEN_EXPIRED_RESPONSE_MESSAGE) {
        await refreshUserToken();
        getUserProfile();
      } else {
        clearAuthData();
      }
    } finally {
      setLoading(false);
    }
  }, [refreshUserToken, clearAuthData, onGetUser]);

  const loginUser = useCallback(
    async ({ login, password }) => {
      const { data } = await onLogin({ login, password });
      setAuthTokens(data);
      getUserProfile();
    },
    [getUserProfile, onLogin],
  );

  const authContextValue = useMemo(
    () => ({
      user,
      loginUser,
      logout,
      refreshUserToken,
      getUserProfile,
    }),
    [user, loginUser, logout, refreshUserToken, getUserProfile],
  );

  useEffect(() => {
    if (!user && hasAuthTokensStored()) {
      getUserProfile();
    }
  }, [getUserProfile, user]);

  if (loading) {
    return <Loader />;
  }

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  return useContext(AuthContext);
}
